/**
 * §28: титры — правка текста, строк и положения.
 *
 * Правим по строкам, а не по словам: набирать тайминг каждого слова руками
 * никто не станет. Изменённая строка переразбивается внутри своего отрезка
 * времени, поэтому пословный тайминг сохраняется и после правки текста.
 */
import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import type { CaptionLine, SceneCaptions } from '@alterium/shared';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';

/** Положение титров: значение и ключ перевода отдельно. */
const positions: Array<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'captions.pos.inherit' },
  { value: 'top', labelKey: 'style.pos.top' },
  { value: 'center', labelKey: 'style.pos.center' },
  { value: 'bottom', labelKey: 'style.pos.bottom' },
];

/**
 * Переразбить строку по словам внутри её отрезка времени.
 *
 * Пропорция по длине слова — та же, что при автосоздании: иначе правка
 * одного слова сдвинула бы подсветку всей строки.
 */
function retimeLine(line: CaptionLine, text: string): CaptionLine | null {
  const tokens = text.split(/\s+/).map((token) => token.trim()).filter(Boolean);
  if (tokens.length === 0) return null;

  const start = line.words[0]!.start;
  const end = line.words.at(-1)!.end;
  const span = Math.max(0.2, end - start);

  const weights = tokens.map((token) => Math.max(1, token.replace(/[^\p{L}\p{N}]/gu, '').length));
  const total = weights.reduce((sum, weight) => sum + weight, 0);

  let cursor = start;
  return {
    words: tokens.map((token, index) => {
      const width = (weights[index]! / total) * span;
      const word = {
        text: token,
        start: Math.round(cursor * 1000) / 1000,
        end: Math.round((cursor + width) * 1000) / 1000,
      };
      cursor += width;
      return word;
    }),
  };
}

const lineToText = (line: CaptionLine): string => line.words.map((word) => word.text).join(' ');

interface CaptionsEditorProps {
  captions: SceneCaptions & { generated?: boolean };
  saving: boolean;
  onSave: (input: { enabled?: boolean; position?: string | null; lines?: CaptionLine[] }) => void;
  onRegenerate: () => void;
}

export function CaptionsEditor({ captions, saving, onSave, onRegenerate }: CaptionsEditorProps) {
  const { t } = useTranslation();
  // Черновики строк живут ровно столько, сколько живёт этот набор титров:
  // при пересборке снаружи компонент перемонтируется по key, и правки
  // сбрасываются сами — синхронизировать состояние эффектом не нужно.
  const [drafts, setDrafts] = useState<string[]>(() => captions.lines.map(lineToText));

  const changed = drafts.some((draft, index) => draft !== lineToText(captions.lines[index]!));

  const save = () => {
    const lines = captions.lines
      .map((line, index) => retimeLine(line, drafts[index] ?? ''))
      .filter((line): line is CaptionLine => line !== null);
    onSave({ lines });
  };

  return (
    <section className="flex flex-col gap-3 rounded-control border border-line bg-surface-secondary p-3.5">
      <div className="flex items-center justify-between">
        <p className="text-meta text-content-secondary">{t('style.captions')}</p>
        <label className="flex items-center gap-2 text-meta text-content-secondary">
          <input
            type="checkbox"
            checked={captions.enabled}
            onChange={(event) => onSave({ enabled: event.target.checked })}
            className="h-4 w-4 accent-accent"
          />{t('captions.show')}</label>
      </div>

      {captions.lines.length === 0 ? (
        <p className="text-meta text-content-muted">{t('captions.noScript')}</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {captions.lines.map((line, index) => (
              <div key={`${line.words[0]!.start}-${index}`} className="flex items-start gap-2">
                <span className="w-11 shrink-0 pt-2.5 text-meta tabular-nums text-content-muted">
                  {t('captions.atSecond', { value: line.words[0]!.start.toFixed(1) })}
                </span>
                <input
                  value={drafts[index] ?? ''}
                  onChange={(event) => {
                    const next = [...drafts];
                    next[index] = event.target.value;
                    setDrafts(next);
                  }}
                  className="h-10 w-full rounded-control border border-line bg-surface px-3 text-body text-content focus:border-accent focus:outline-none"
                />
              </div>
            ))}
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-meta text-content-secondary">{t('style.position')}</span>
            <select
              value={captions.position ?? ''}
              onChange={(event) => onSave({ position: event.target.value || null })}
              className="h-10 rounded-control border border-line bg-surface px-3 text-body text-content focus:border-accent focus:outline-none"
            >
              {positions.map((position) => (
                <option key={position.value} value={position.value}>
                  {t(position.labelKey)}
                </option>
              ))}
            </select>
          </label>

          {/* Пользователь должен понимать, насколько точен тайминг. */}
          <p className="text-meta text-content-muted">
            {captions.source === 'manual'
              ? t('captions.manual')
              : captions.source === 'provider'
                ? t('captions.fromVoice')
                : t('captions.estimated')}
          </p>

          <div className="flex gap-3">
            <Button variant="secondary" disabled={saving || !changed} onClick={save}>
              {saving ? t('common.saving') : t('captions.save')}
            </Button>
            <Button variant="tertiary" disabled={saving} onClick={onRegenerate}>
              <RotateCcw className="h-4 w-4" strokeWidth={1.75} />{t('captions.rebuild')}</Button>
          </div>
        </>
      )}
    </section>
  );
}
